/**
 * Launch-market state / governorate / emirate seed (CR 2026-05-10).
 *
 * **TEMPORARY INLINE SUBSET** — UAE / Jordan / Saudi only.
 *
 * Per the technical-research dispatch on 2026-05-10:
 *   - Bundling all 5,000 global states would add ~80 KB gz — tolerable
 *     but unnecessary; only the launch markets benefit from the picker
 *     today.
 *   - Full city data (~150k rows, ~6 MB gz) is **infeasible** to bundle
 *     and will land as a BE-driven `/api/v2/locations/cities/search`
 *     endpoint with debounced autocomplete in a follow-up PBI.
 *   - Long-term plan: extend AB#1518 master tables with `States` +
 *     `Cities` and replace this inline seed with `useCountryStates`
 *     hook (TanStack Query, `staleTime: Infinity`).
 *
 * For countries NOT in this seed, the State field gracefully falls back
 * to the existing freeform text input.
 *
 * AR names follow Unicode CLDR (the same source `i18n-iso-countries` uses)
 * — arabic-translator skill should re-validate before launch.
 */

export interface StateSeed {
    /** ISO 3166-2 subdivision code, e.g. 'AE-DU' for Dubai. */
    code: string;
    nameEn: string;
    nameAr: string;
}

/**
 * Map from ISO-3166-1 alpha-2 country code → ordered list of states.
 * Order matches the official subdivision list per ISO 3166-2 / each
 * country's local convention (largest population / capital first).
 */
export const LAUNCH_STATES_SEED: Readonly<Record<string, readonly StateSeed[]>> = {
    AE: [
        { code: 'AE-AZ', nameEn: 'Abu Dhabi', nameAr: 'أبوظبي' },
        { code: 'AE-DU', nameEn: 'Dubai', nameAr: 'دبي' },
        { code: 'AE-SH', nameEn: 'Sharjah', nameAr: 'الشارقة' },
        { code: 'AE-AJ', nameEn: 'Ajman', nameAr: 'عجمان' },
        { code: 'AE-FU', nameEn: 'Fujairah', nameAr: 'الفجيرة' },
        { code: 'AE-RK', nameEn: 'Ras Al Khaimah', nameAr: 'رأس الخيمة' },
        { code: 'AE-UQ', nameEn: 'Umm Al Quwain', nameAr: 'أم القيوين' },
    ],
    JO: [
        { code: 'JO-AM', nameEn: 'Amman', nameAr: 'عمّان' },
        { code: 'JO-IR', nameEn: 'Irbid', nameAr: 'إربد' },
        { code: 'JO-AZ', nameEn: 'Zarqa', nameAr: 'الزرقاء' },
        { code: 'JO-BA', nameEn: 'Balqa', nameAr: 'البلقاء' },
        { code: 'JO-MD', nameEn: 'Madaba', nameAr: 'مادبا' },
        { code: 'JO-KA', nameEn: 'Karak', nameAr: 'الكرك' },
        { code: 'JO-AT', nameEn: 'Tafilah', nameAr: 'الطفيلة' },
        { code: 'JO-MN', nameEn: "Ma'an", nameAr: 'معان' },
        { code: 'JO-AQ', nameEn: 'Aqaba', nameAr: 'العقبة' },
        { code: 'JO-MA', nameEn: 'Mafraq', nameAr: 'المفرق' },
        { code: 'JO-JA', nameEn: 'Jerash', nameAr: 'جرش' },
        { code: 'JO-AJ', nameEn: 'Ajloun', nameAr: 'عجلون' },
    ],
    SA: [
        { code: 'SA-01', nameEn: 'Riyadh', nameAr: 'الرياض' },
        { code: 'SA-02', nameEn: 'Makkah', nameAr: 'مكة المكرمة' },
        { code: 'SA-03', nameEn: 'Madinah', nameAr: 'المدينة المنورة' },
        { code: 'SA-04', nameEn: 'Eastern Province', nameAr: 'المنطقة الشرقية' },
        { code: 'SA-05', nameEn: 'Qassim', nameAr: 'القصيم' },
        { code: 'SA-06', nameEn: 'Hail', nameAr: 'حائل' },
        { code: 'SA-07', nameEn: 'Tabuk', nameAr: 'تبوك' },
        { code: 'SA-08', nameEn: 'Northern Borders', nameAr: 'الحدود الشمالية' },
        { code: 'SA-09', nameEn: 'Jazan', nameAr: 'جازان' },
        { code: 'SA-10', nameEn: 'Najran', nameAr: 'نجران' },
        { code: 'SA-11', nameEn: 'Al Bahah', nameAr: 'الباحة' },
        { code: 'SA-12', nameEn: 'Al Jouf', nameAr: 'الجوف' },
        { code: 'SA-14', nameEn: 'Asir', nameAr: 'عسير' },
    ],
};

/** `null` = no curated list; caller should fall back to freeform input. */
export function getStatesForCountry(
    iso: string | undefined | null,
): readonly StateSeed[] | null {
    if (!iso) return null;
    const upper = iso.trim().toUpperCase();
    return LAUNCH_STATES_SEED[upper] ?? null;
}

/**
 * UI label for the State field, country-aware:
 *   - AE  → "Emirate"
 *   - JO  → "Governorate"
 *   - SA  → "Region"
 *   - else → "State / Governorate"
 *
 * Always suffixed with "(optional)" — the BE accepts an empty value
 * after PR #735.
 */
export function getStateLabel(iso: string | undefined | null): string {
    const upper = (iso ?? '').trim().toUpperCase();
    switch (upper) {
        case 'AE':
            return 'Emirate (optional)';
        case 'JO':
            return 'Governorate (optional)';
        case 'SA':
            return 'Region (optional)';
        default:
            return 'State / Governorate (optional)';
    }
}
